import React, { useState } from 'react';
import { Box } from '@mui/material';

interface NavbarProps {
  activeTab: 'home' | 'explorer' | 'cell-panel-selector' | 'enrichment' | 'about';
  onTabChange: (tab: 'home' | 'explorer' | 'cell-panel-selector' | 'enrichment' | 'about') => void;
}

const Navbar: React.FC<NavbarProps> = ({ activeTab, onTabChange }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      <nav
        style={{
          position: 'fixed',
          zIndex: 50,
          transition: 'all 300ms',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(12px)',
          border: '1px solid #e5e7eb',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          top: '8px',
          left: '8px',
          right: '8px',
          borderRadius: '16px',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3, md: 4, lg: 6 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: { xs: '56px', sm: '64px' } }}>
            {/* Logo */}
            <a 
              href="/" 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                textDecoration: 'none',
                color: 'inherit',
                minWidth: 0
              }}
            >
              <Box
                component="img"
                src="/cysteine_logo.png"
                alt="Cysteine.app Logo"
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  borderRadius: '50%',
                }}
              />
              <span
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#111827',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Cysteine.app
              </span>
            </a>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={() => onTabChange('home')}
                  style={{
                    color: activeTab === 'home' ? '#111827' : '#4b5563',
                    backgroundColor: activeTab === 'home' ? '#f3f4f6' : 'transparent',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 200ms',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== 'home') {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== 'home') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: '#6b7280' }}
                  >
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                  <span>Home</span>
                </button>

                <button
                  onClick={() => onTabChange('explorer')}
                  style={{
                    color: activeTab === 'explorer' ? '#111827' : '#4b5563',
                    backgroundColor: activeTab === 'explorer' ? '#f3f4f6' : 'transparent',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 200ms',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== 'explorer') {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== 'explorer') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: '#6b7280' }}
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="M21 21l-4.35-4.35"></path>
                  </svg>
                  <span>Explorer</span>
                </button>

                <button
                  onClick={() => onTabChange('cell-panel-selector')}
                  style={{
                    color: activeTab === 'cell-panel-selector' ? '#111827' : '#4b5563',
                    backgroundColor: activeTab === 'cell-panel-selector' ? '#f3f4f6' : 'transparent',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 200ms',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== 'cell-panel-selector') {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== 'cell-panel-selector') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: '#6b7280' }}
                  >
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                  <span>Cell Panel Selector</span>
                </button>

                <button
                  onClick={() => onTabChange('enrichment')}
                  style={{
                    color: activeTab === 'enrichment' ? '#111827' : '#4b5563',
                    backgroundColor: activeTab === 'enrichment' ? '#f3f4f6' : 'transparent',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 200ms',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== 'enrichment') {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== 'enrichment') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: '#6b7280' }}
                  >
                    <path d="M3 3v18h18"></path>
                    <path d="M18 9l-5 5-2-2-4 4"></path>
                  </svg>
                  <span>Enrichment</span>
                </button>

                <button
                  onClick={() => onTabChange('about')}
                  style={{
                    color: activeTab === 'about' ? '#111827' : '#4b5563',
                    backgroundColor: activeTab === 'about' ? '#f3f4f6' : 'transparent',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 200ms',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== 'about') {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== 'about') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: '#6b7280' }}
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 16v-4"></path>
                    <path d="M12 8h.01"></path>
                  </svg>
                  <span>About</span>
                </button>
              </Box>
            </Box>

            {/* Right side - Visit the Zaro Lab button */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '12px' }}>
              <a
                href="https://pharm.ucsf.edu/zaro"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <button
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    backgroundColor: '#052049',
                    color: '#ffffff',
                    padding: '8px 20px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 200ms',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0E5258';
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#052049';
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                  }}
                >
                  <img
                    src="/ucsf-logo.png"
                    alt="UCSF"
                    style={{
                      height: '20px',
                      width: 'auto',
                      filter: 'brightness(0) invert(1)',
                    }}
                  />
                  <span>Visit the Zaro Lab</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M7 7h10v10"></path>
                    <path d="M7 17L17 7"></path>
                  </svg>
                </button>
              </a>
            </Box>

            {/* Mobile menu button */}
            <Box sx={{ display: { md: 'none' } }}>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                  borderRadius: '12px',
                  color: '#4b5563',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 200ms',
                  WebkitTapHighlightColor: 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.color = '#111827';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#4b5563';
                }}
                aria-label="Toggle menu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="4" x2="20" y1="12" y2="12"></line>
                  <line x1="4" x2="20" y1="6" y2="6"></line>
                  <line x1="4" x2="20" y1="18" y2="18"></line>
                </svg>
              </button>
            </Box>
          </Box>
        </Box>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: '80px',
            left: '8px',
            right: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(12px)',
            border: '1px solid #e5e7eb',
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            p: 2,
            zIndex: 49,
            display: { md: 'none' },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <button
              onClick={() => {
                onTabChange('home');
                setMobileMenuOpen(false);
              }}
              style={{
                color: activeTab === 'home' ? '#111827' : '#4b5563',
                backgroundColor: activeTab === 'home' ? '#f3f4f6' : 'transparent',
                padding: '12px 16px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 200ms',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                border: 'none',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: '#6b7280' }}
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              <span>Home</span>
            </button>

            <button
              onClick={() => {
                onTabChange('explorer');
                setMobileMenuOpen(false);
              }}
              style={{
                color: activeTab === 'explorer' ? '#111827' : '#4b5563',
                backgroundColor: activeTab === 'explorer' ? '#f3f4f6' : 'transparent',
                padding: '12px 16px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 200ms',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                border: 'none',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: '#6b7280' }}
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="M21 21l-4.35-4.35"></path>
              </svg>
              <span>Explorer</span>
            </button>

            <button
              onClick={() => {
                onTabChange('cell-panel-selector');
                setMobileMenuOpen(false);
              }}
              style={{
                color: activeTab === 'cell-panel-selector' ? '#111827' : '#4b5563',
                backgroundColor: activeTab === 'cell-panel-selector' ? '#f3f4f6' : 'transparent',
                padding: '12px 16px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 200ms',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                border: 'none',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: '#6b7280' }}
              >
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
              <span>Cell Panel Selector</span>
            </button>

            <button
              onClick={() => {
                onTabChange('enrichment');
                setMobileMenuOpen(false);
              }}
              style={{
                color: activeTab === 'enrichment' ? '#111827' : '#4b5563',
                backgroundColor: activeTab === 'enrichment' ? '#f3f4f6' : 'transparent',
                padding: '12px 16px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 200ms',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                border: 'none',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: '#6b7280' }}
              >
                <path d="M3 3v18h18"></path>
                <path d="M18 9l-5 5-2-2-4 4"></path>
              </svg>
              <span>Enrichment</span>
            </button>

            <button
              onClick={() => {
                onTabChange('about');
                setMobileMenuOpen(false);
              }}
              style={{
                color: activeTab === 'about' ? '#111827' : '#4b5563',
                backgroundColor: activeTab === 'about' ? '#f3f4f6' : 'transparent',
                padding: '12px 16px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 200ms',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                border: 'none',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: '#6b7280' }}
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 16v-4"></path>
                <path d="M12 8h.01"></path>
              </svg>
              <span>About</span>
            </button>

            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e5e7eb' }}>
              <a
                href="https://pharm.ucsf.edu/zaro"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <button
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    backgroundColor: '#052049',
                    color: '#ffffff',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 200ms',
                    width: '100%',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0E5258';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#052049';
                  }}
                >
                  <img
                    src="/ucsf-logo.png"
                    alt="UCSF"
                    style={{
                      height: '20px',
                      width: 'auto',
                      filter: 'brightness(0) invert(1)',
                    }}
                  />
                  <span>Visit the Zaro Lab</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M7 7h10v10"></path>
                    <path d="M7 17L17 7"></path>
                  </svg>
                </button>
              </a>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Navbar;